import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_PLAYER_RATIO, SQUARE_RATIO } from '../constants/editorConst';
import * as screenfull from 'screenfull';
import { VIDEO_ORIENTATIONS } from '../utils/SimulatedItemsUtility';
import { throttle } from 'engage/utils/general';

const PLAYER_BORDER_WIDTH = 2;

export default function useVideoContentDimensions({
  parentNode,
  id,
  maxDimensions = null,
  mixedOrientation = false,
  multiplyMode = false,
  isEmptyVideo = false,
  isLandscapePreview = false,
  isPortraitPreview = false,
  isSquarePreview = false,
}) {
  const [offsetWidth, setOffsetWidth] = useState(0);
  const [offsetHeight, setOffsetHeight] = useState(0);
  const [videoContentWidth, setVideoContentWidth] = useState(0);
  const [videoContentHeight, setVideoContentHeight] = useState(0);
  const [scaleRatio, setScaleRatio] = useState(null);
  const [smallestSide, setSmallestSide] = useState(0);

  const [orientation, setOrientation] = useState(VIDEO_ORIENTATIONS.portrait);
  const [loading, setLoading] = useState(false);

  const calculationTimeout = useRef(null);
  const resizeObserverRef = useRef(null);

  const isFullscreenView = (videoWidth, videoHeight) => {
    if (screenfull.isFullscreen) {
      return true;
    }
    if (window.innerWidth - videoWidth <= PLAYER_BORDER_WIDTH && window.innerHeight - videoHeight <= PLAYER_BORDER_WIDTH) {
      return true;
    }
    return false;
  };

  const getVideoDimensions = (container, mixedOrientation, maxDimensions, tries = 0) => {
    let videos = [];
    if (id) {
      if (container) {
        // If the video is empty, then we set the videoWidth and videoHeight of the video tag
        // to the same as the container (full size), since the video tag will not have its own values
        const video = {}
        if (isEmptyVideo || isLandscapePreview) {
          video.offsetWidth = container?.offsetWidth;
          video.offsetHeight = container?.offsetHeight;

          video.videoWidth = container?.offsetWidth;
          video.videoHeight = container?.offsetHeight;
          videos = [video];
        } else if (isPortraitPreview || isSquarePreview) {
          // Simulate a full player dimension for preview
          video.offsetWidth = container?.offsetHeight * DEFAULT_PLAYER_RATIO;
          video.offsetHeight = container?.offsetHeight;
          
          video.videoWidth = container?.offsetWidth;
          video.videoHeight = container?.offsetHeight;
          videos = [video];
        } else {
          videos = container.querySelectorAll('video');
        }
      }
    } else {
      videos = document.querySelectorAll('video');
    }

    const video = Array.from(videos).find(video => video && video.videoHeight > 0 && video.offsetHeight > 0);

    if (!video) {
      tries < 10 && setTimeout(() => {
        tries = tries + 1;
        getVideoDimensions(container, mixedOrientation, maxDimensions, tries);
      }, [500]);
      return;
    }

    const isFullScreen = isFullscreenView(video?.offsetWidth, video?.offsetHeight);
    // Calculate video ratio (without black background)
    let videoRatio = video.videoWidth / video.videoHeight;

    let contentWidth = null;
    let contentHeight = null;

    if (multiplyMode) {
      contentWidth = container?.offsetWidth;
      contentHeight = container?.offsetHeight;

      setOffsetWidth(contentWidth);
      setOffsetHeight(contentHeight);

      if (mixedOrientation) {
        if (mixedOrientation === VIDEO_ORIENTATIONS.square) {
          contentWidth = container.offsetHeight;
        }
      } else {
        if (maxDimensions?.width) {
          contentWidth = container.offsetHeight * maxDimensions?.width;
        } else if (maxDimensions?.height) {
          contentHeight = container.offsetWidth / maxDimensions?.height;
        } else if (videoRatio < DEFAULT_PLAYER_RATIO) {
          contentWidth = container.offsetHeight * videoRatio;
        } else {
          contentHeight = container.offsetWidth / videoRatio;
        }
      }
    } else {
      contentWidth = video.offsetWidth;
      contentHeight = video.offsetHeight;

      setOffsetWidth(contentWidth);
      setOffsetHeight(contentHeight);

      if (mixedOrientation) {
        // Square mixed orientation should be applied when:
        // - non-fullscreen for desktop (!isFullScreen)
        // - non-fullscreen for mobile (!isFullScreen)
        // - fullscreen in mobile only in portrait mode (isFullScreen && width > height)
        // For fullscreen in mobile portrait device configuration full screen size should be applied for simulated layouts
        if ((!isFullScreen || (isFullScreen && window.innerWidth > window.innerHeight)) && mixedOrientation === VIDEO_ORIENTATIONS.square) {
          if (video.offsetWidth > video.offsetHeight) {
            contentWidth = video.offsetHeight;
          } else {
            contentHeight = video.offsetWidth;
          }
        }
      } else {
        if (maxDimensions?.width) {
          if (isFullScreen) {
            if (videoRatio < 1) {
              // Portrait
              const height = video.offsetWidth / videoRatio;
              if (height > video.offsetHeight) {
                contentWidth = video.offsetHeight * maxDimensions?.width;
              } else {
                contentHeight = height;
              }
            } else if (videoRatio === 1) {
              // Square
              if (video.offsetWidth > video.offsetHeight) {
                contentWidth = video.offsetHeight;
              } else {
                contentHeight = video.offsetWidth;
              }
            } else {
              // Landscape
              const height = video.offsetWidth / maxDimensions?.width;
              if (height > video.offsetHeight) {
                contentWidth = video.offsetHeight * maxDimensions?.width;
              } else {
                contentHeight = height;
              }
            }
          } else {
            contentWidth = video.offsetHeight * maxDimensions?.width;
          }
        } else if (maxDimensions?.height) {
          if (isFullScreen) {
            contentHeight = video.offsetWidth / videoRatio;
          } else {
            contentHeight = video.offsetWidth / maxDimensions?.height;
          }
        } else if (videoRatio < DEFAULT_PLAYER_RATIO) {
          if (isFullScreen) {
            const height = video.offsetWidth / videoRatio;
            if (height > video.offsetHeight) {
              contentWidth = video.offsetHeight * videoRatio;
            } else {
              contentHeight = height;
            }
          } else {
            contentWidth = video.offsetHeight * videoRatio;
          }
        } else {
          if (isFullScreen) {
            const height = video.offsetWidth / videoRatio;
            if (height > video.offsetHeight) {
              contentWidth = video.offsetHeight * videoRatio;
            } else {
              contentHeight = height;
            }
          } else {
            contentHeight = video.offsetWidth / videoRatio;
          }
        }
      }
    }

    setVideoContentWidth(contentWidth);
    setVideoContentHeight(contentHeight);

    // Calculate video ratio (without black background)
    if (mixedOrientation) {
      if (mixedOrientation === VIDEO_ORIENTATIONS.landscape) {
        setScaleRatio(9 / 16);
        setOrientation(VIDEO_ORIENTATIONS.landscape);
      } else {
        setScaleRatio(9 / 12);
        setOrientation(VIDEO_ORIENTATIONS.square);
      }
    } else {
      if (maxDimensions?.width === SQUARE_RATIO) {
        setScaleRatio(9 / 12);
        setOrientation(VIDEO_ORIENTATIONS.square);
      } else if (maxDimensions?.width > SQUARE_RATIO || maxDimensions?.height > SQUARE_RATIO) {
        setScaleRatio(9 / 16);
        setOrientation(VIDEO_ORIENTATIONS.landscape);
      } else if (video.videoWidth === video.videoHeight) {
        setScaleRatio(9 / 12);
        setOrientation(VIDEO_ORIENTATIONS.square);
      } else if (video.videoWidth > video.videoHeight) {
        setScaleRatio(9 / 16);
        setOrientation(VIDEO_ORIENTATIONS.landscape);
      } else {
        setScaleRatio(video.videoWidth / video.videoHeight);
        setOrientation(VIDEO_ORIENTATIONS.portrait);
      }
    }

    setSmallestSide(Math.min(contentHeight, contentWidth));
    calculationTimeout.current = null;
  };

  useEffect(() => {
    if (parentNode instanceof Element) {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }

      getVideoDimensions(parentNode, mixedOrientation, maxDimensions);

      resizeObserverRef.current = new ResizeObserver(throttle((entries) => {
        for (const entry of entries) {
          getVideoDimensions(entry.target, mixedOrientation, maxDimensions);
        }
      }));
      resizeObserverRef.current.observe(parentNode);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [parentNode, id, mixedOrientation, maxDimensions]);

  return {
    loading,
    setLoading,
    offsetHeight,
    offsetWidth,
    smallestSide,
    videoContentWidth,
    videoContentHeight,
    scaleRatio,
    orientation,
  };
}
